/** @file viewportLabels: the viewport's text labels (lock reason, drag readout, yaw tick), moved in place. */

/** A label's text and where it goes (CSS px in the viewport box): centred on the point, or beside the cursor. */
export type LabelSpot = { text: string; at: readonly [number, number] };

/** This frame's labels; null hides one. */
export type LabelsInput = { lock: LabelSpot | null; readout: LabelSpot | null; tick: LabelSpot | null };

/** `update` once per frame (null hides every label); `dispose` removes the elements. */
export type ViewportLabels = { update(input: LabelsInput | null): void; dispose(): void };

/** One label element: shows a spot, touching the DOM only when its text or position changed. */
type Label = { el: HTMLElement; show(spot: LabelSpot | null): void };

const BASE = {
  position: 'absolute', left: '0px', top: '0px', pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: '1',
  font: '11px system-ui, sans-serif', padding: '1px 5px', borderRadius: '3px',
} as const;

/** Per kind: colours and where the box sits relative to its point (`shift` is a CSS translate after the point). */
const KINDS = {
  lock: { style: { color: '#f2b544', background: '#141a17d9', border: '1px solid #f2b54466' }, shift: 'translate(-50%, -50%)' },
  readout: { style: { color: '#d8dee9', background: '#141a17d9' }, shift: 'translate(14px, 14px)' },
  tick: { style: { color: '#6ca8e0', background: '#141a17bf', font: '600 10px system-ui, sans-serif', padding: '0px 3px' }, shift: 'translate(-50%, -50%)' },
} as const;

/** A hidden label of `kind` appended to `container`. */
function label(container: HTMLElement, kind: keyof typeof KINDS): Label {
  const el = container.ownerDocument.createElement('div');
  el.dataset['viewportLabel'] = kind;
  Object.assign(el.style, BASE, KINDS[kind].style);
  el.hidden = true;
  container.appendChild(el);
  let shown = { text: '', x: NaN, y: NaN };
  return {
    el,
    show: (spot) => {
      el.hidden = spot === null;
      if (spot === null) return;
      if (spot.text !== shown.text) el.textContent = spot.text;
      const [x, y] = [Math.round(spot.at[0]), Math.round(spot.at[1])];
      if (x !== shown.x || y !== shown.y) el.style.transform = `translate(${String(x)}px, ${String(y)}px) ${KINDS[kind].shift}`;
      shown = { text: spot.text, x, y };
    },
  };
}

/**
 * Three absolutely placed elements over the canvas, created once with the viewport (text cannot be a GPU mesh
 * without a 2D canvas texture; these are not re-created or re-laid out per frame, only moved when their point moves).
 */
export function viewportLabels(container: HTMLElement): ViewportLabels {
  const labels = { lock: label(container, 'lock'), readout: label(container, 'readout'), tick: label(container, 'tick') };
  return {
    update: (input) => {
      labels.lock.show(input?.lock ?? null);
      labels.readout.show(input?.readout ?? null);
      labels.tick.show(input?.tick ?? null);
    },
    dispose: () => { for (const l of Object.values(labels)) l.el.remove(); },
  };
}
